import React from 'react';
import {
  Box,
  Container,
  Paper,
  Title,
  Badge,
  Group,
  Stack,
  Loader,
  Text,
} from '@mantine/core';
import { ErrorDisplay } from '../common/ErrorDisplay';
import { usePublicItinerary } from '../../hooks/usePublicItinerary';
import { useLocaleDateFormatter } from '../../hooks/useLocaleDateFormatter';
import {
  STATUS_LABELS,
  VISIBILITY_LABELS,
  OCCASION_LABELS,
} from '../../constants';
import {
  STATUS_COLORS,
  OCCASION_COLORS,
  OCCASION_DEFAULT_COLORS,
} from '../../theme/tokens';
import { resolveDates } from '../../utils/trips/resolveDates';
import { buildTravelerViews } from '../../utils/trips/travelerColorsUtils';
import { CombinedTimeline } from './CombinedTimeline';

export interface PublicItineraryProps {
  readonly shareToken: string;
}

const PublicItinerary: React.FC<PublicItineraryProps> = ({ shareToken }) => {
  const { trip, isLoading, error } = usePublicItinerary(shareToken);
  const { formatDisplayDate } = useLocaleDateFormatter();

  if (isLoading) {
    return (
      <Container size="lg" mt="xl" ta="center" data-cy="public-itinerary">
        <Loader />
      </Container>
    );
  }

  if (error || !trip) {
    return (
      <Container size="lg" mt="xl" data-cy="public-itinerary">
        <ErrorDisplay error={error} />
      </Container>
    );
  }

  const resolvedDates = resolveDates(trip);
  const travelers = buildTravelerViews(trip.memberships ?? [], undefined);
  const primaryOccasion = trip.occasions[0];
  const occasionColors =
    (primaryOccasion && OCCASION_COLORS[primaryOccasion]) ||
    OCCASION_DEFAULT_COLORS;
  const statusColors = STATUS_COLORS[trip.status] ?? STATUS_COLORS.PLANNING;

  const dateLabel =
    resolvedDates.source === 'none'
      ? 'No dates yet'
      : `${formatDisplayDate(resolvedDates.start!)} – ${formatDisplayDate(resolvedDates.end!)}${
          resolvedDates.source === 'itinerary' ? ' (itinerary)' : ''
        }`;

  return (
    <Container size="lg" mt="xl" mb="xl" data-cy="public-itinerary">
      <Stack gap="lg">
        <Paper shadow="sm" p="lg" radius="md" withBorder>
          <Box
            h={42}
            mb="md"
            px="sm"
            style={{
              backgroundColor: occasionColors.coverBg,
              borderRadius: 'var(--mantine-radius-sm)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Group gap={6}>
              {trip.occasions.length > 0 ? (
                trip.occasions.map(id => (
                  <Badge
                    key={id}
                    size="sm"
                    styles={{
                      root: {
                        backgroundColor: (
                          OCCASION_COLORS[id] ?? OCCASION_DEFAULT_COLORS
                        ).badgeFill,
                        color: (OCCASION_COLORS[id] ?? OCCASION_DEFAULT_COLORS)
                          .badgeText,
                      },
                    }}
                  >
                    {OCCASION_LABELS[id] ?? id}
                  </Badge>
                ))
              ) : (
                <Badge size="sm">Trip</Badge>
              )}
            </Group>
            <Badge
              size="sm"
              styles={{
                root: {
                  backgroundColor: statusColors.bg,
                  color: statusColors.text,
                },
              }}
            >
              {STATUS_LABELS[trip.status] ?? trip.status}
            </Badge>
          </Box>

          <Title order={2} mb="xs">
            {trip.name}
          </Title>
          {trip.description && (
            <Text c="dimmed" mb="md">
              {trip.description}
            </Text>
          )}

          <Group gap="sm" wrap="wrap">
            <Badge variant="light">{dateLabel}</Badge>
            <Badge variant="outline">
              {VISIBILITY_LABELS[trip.visibility] ?? trip.visibility}
            </Badge>
            {trip.derivedCities.length > 0 && (
              <Badge variant="outline">{trip.derivedCities.join(' · ')}</Badge>
            )}
          </Group>
        </Paper>

        <Paper shadow="sm" p="lg" radius="md" withBorder>
          <Title order={3} mb="md">
            Combined Timeline
          </Title>
          <CombinedTimeline
            travelers={travelers}
            derivedStartDate={trip.derivedStartDate}
            derivedEndDate={trip.derivedEndDate}
          />
        </Paper>
      </Stack>
    </Container>
  );
};

export default PublicItinerary;
